/**
 * Hooks des offres et des droits (docs/10 §9).
 *
 * ⚠️ **`useEntitlements` ne protège rien.** Cacher un bouton d'upload n'empêche
 * pas d'appeler la route : le backend revérifie systématiquement, et chaque
 * capacité masquée ici a son test d'API en face. Ce que ces hooks servent, c'est
 * d'éviter à l'utilisateur de découvrir un refus après avoir rempli un
 * formulaire.
 *
 * ⚠️ **`has()` ne se lit pas sur la valeur brute.** `0` (quota nul) et `null`
 * (illimité) sont tous deux « faux » en JavaScript, et un `if (!value)` écrit
 * de bonne foi ouvrirait en grand la restriction la plus stricte du catalogue.
 * C'est pourquoi le serveur envoie `type` avec chaque valeur, et pourquoi
 * l'accès passe par les fonctions ci-dessous plutôt que par un `Record`.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { billingKeys } from "@/shared/api/query-keys";

export type Plan = components["schemas"]["PlanResponse"];
export type PlanPrice = components["schemas"]["PlanPriceResponse"];
export type FeatureValue = components["schemas"]["FeatureValueResponse"];
export type Entitlements = components["schemas"]["EntitlementsResponse"];
export type Subscription = components["schemas"]["SubscriptionResponse"];
export type Usage = components["schemas"]["UsageResponse"];

/** Ce que `useEntitlements` rend, une fois les droits indexés par code. */
export interface EntitlementsView {
  isPending: boolean;
  isError: boolean;
  error: ApiError | null;
  planCode: string | undefined;
  /** La capacité booléenne est-elle ouverte ? Faux tant que les droits chargent. */
  has: (code: string) => boolean;
  /** La borne d'un quota ou d'un plafond. `null` = illimité, `undefined` = inconnue. */
  limitOf: (code: string) => number | null | undefined;
  feature: (code: string) => FeatureValue | undefined;
}

export function usePlans(): UseQueryResult<Plan[], ApiError> {
  return useQuery({
    queryKey: billingKeys.plans(),
    queryFn: async () => (await unwrap(api.GET("/api/v1/billing/plans", {}))).plans,
  });
}

export function useSubscription(): UseQueryResult<Subscription, ApiError> {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => unwrap(api.GET("/api/v1/billing/subscription", {})),
  });
}

export function useUsage(): UseQueryResult<Usage[], ApiError> {
  return useQuery({
    queryKey: billingKeys.usage(),
    queryFn: async () => (await unwrap(api.GET("/api/v1/billing/usage", {}))).items,
  });
}

/**
 * Les droits effectifs de l'appelant.
 *
 * Rend `has(code) === false` pendant le chargement, et c'est **le seul défaut
 * sûr** : le contraire ferait apparaître une fonctionnalité payante une
 * fraction de seconde à chaque rafraîchissement, puis la retirerait. Les écrans
 * qui veulent distinguer « pas encore chargé » de « pas ouvert » lisent
 * `isPending`.
 */
export function useEntitlements(): EntitlementsView {
  const query = useQuery<Entitlements, ApiError>({
    queryKey: billingKeys.entitlements(),
    queryFn: () => unwrap(api.GET("/api/v1/billing/entitlements", {})),
  });

  const byCode = new Map((query.data?.features ?? []).map((item) => [item.code, item]));

  return {
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    planCode: query.data?.plan_code,
    has: (code) => {
      const found = byCode.get(code);
      // `=== true` et non une conversion : sur un quota, `value` vaut `5000`,
      // qui serait « vrai » et ferait passer un quota pour une capacité ouverte.
      return found?.type === "boolean" && found.value === true;
    },
    limitOf: (code) => {
      const found = byCode.get(code);
      if (found === undefined || found.type === "boolean") {
        return undefined;
      }
      // `?? null` distingue « illimité » (le serveur envoie `null`) d'« inconnu »
      // (`undefined`, la feature n'est pas au catalogue). Les fondre en un seul
      // rendrait l'illimité pour une feature qui n'existe pas.
      return found.value === null ? null : (found.value as number);
    },
    feature: (code) => byCode.get(code),
  };
}

/**
 * Annule son abonnement.
 *
 * Invalide **toute** la racine `billing` : l'annulation change les droits, la
 * page des offres (`is_current`) et l'abonnement. N'invalider que ce qu'on a
 * sous les yeux laisserait les deux autres écrans afficher un état périmé.
 */
export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation<Subscription, ApiError, { atPeriodEnd: boolean }>({
    mutationFn: ({ atPeriodEnd }) =>
      unwrap(
        api.POST("/api/v1/billing/subscription/cancel", {
          body: { at_period_end: atPeriodEnd },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: billingKeys.all }),
  });
}
